import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { projectNav } from '@/components/px/nav'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { GatesPanel } from '@/features/phase-gates/components/GatesPanel'
import { openGates } from '@/features/phase-gates/slices/phaseGatesSlice'
import {
  closeProject,
  openProject,
  selectProjectDetail
} from '@/features/projects/slices/projectDetailSlice'
import { BoardVerdictActions } from '@/features/board/components/BoardVerdictActions'
import { BuildQueue } from '@/features/board/components/BuildQueue'
import { ReviewCard } from '@/features/board/components/ReviewCard'
import { RunLogPanel } from '@/features/board/components/RunLogPanel'
import { useBoardView } from '@/features/board/hooks/useBoardView'
import { selectBoardHasTasks, selectSelectedTask } from '@/features/board/slices/boardSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { projectOverviewRail } from './ProjectOverviewRail'

/**
 * SURFACE 3.5 — BUILD BOARD (`/project/:projectId/build`), WIRED (PX-W7.4,
 * TASK-41CE1DE2): "agents scaffold overnight; a named human merges". The
 * queue, the artefact under review and the four verdicts read and write the
 * real board backend (PX-W4.1) — the merge is real, not a fixture act.
 *
 * The Build gate card composes the LIVE `features/phase-gates` slice — ticket
 * gates roll up into the Build gate rather than being re-derived here. This
 * page dispatches the shared, guarded `openGates` itself (fix cycle 1): it
 * used to rely on the project overview's load, but the overview's unmount
 * runs `gatesClosed()`, so after a hop from the overview (and on any cold
 * load) the card always said "no gates returned". `openGates`'s own
 * `shouldFetch` is the dedupe; this page owns no reset for that slice (the
 * RequirementsRegisterPage precedent).
 */
export default function BuildBoardPage() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId } = useParams<{ projectId: string }>()
  const project = useAppSelector(selectProjectDetail)
  const selectedTask = useAppSelector(selectSelectedTask)
  const boardHasTasks = useAppSelector(selectBoardHasTasks)
  const { view } = useBoardView()

  // The project detail (breadcrumb, rail, and Pull to board's write gate).
  // `openProject` is guarded by `shouldFetch`, but a hop from the overview
  // DOES re-read it once: the overview's unmount cleanup runs
  // `closeProject()`, which invalidates the cache before this page mounts.
  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openProject({ orgId, projectId }))
    return () => {
      dispatch(closeProject())
    }
  }, [dispatch, orgId, projectId])

  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openGates({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  const nav = projectNav(projectOverviewRail(projectId, project))

  return (
    <AppShell
      moduleLabel={t('board.moduleLabel')}
      breadcrumb={project?.name ?? projectId ?? ''}
      contextPill={t('board.contextPill')}
      nav={nav}
    >
      <header className="max-w-4xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{t('board.pageTitle')}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">{t('board.pageStandfirst')}</p>
      </header>

      {orgId !== null && projectId !== undefined && (
        <section className="mt-4" aria-label={t('gates.panelTitle')}>
          <GatesPanel orgId={orgId} projectId={projectId} />
        </section>
      )}

      <section className="mt-6">
        <BuildQueue />
      </section>

      {/* CR-BUILD-BOARD-EPICS-2026-09-26 (design choice 1): the review panels
          serve the Tasks view only — the Epics view has no queue to select
          from. The selection itself lives in the slice and survives a toggle.
          Fix cycle 1: and only once the board holds tasks — never under the
          loading, empty, blocked or failed states (selectBoardHasTasks). */}
      {view === 'tasks' && boardHasTasks && (
        <section className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div>
            <ReviewCard />
            {selectedTask !== null && <BoardVerdictActions />}
          </div>
          <div>
            <RunLogPanel />
          </div>
        </section>
      )}
    </AppShell>
  )
}
